import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { useApiMutation } from "@/hooks/useApiMutation";

const CURRENCIES = [
  { code: "USD", name: "US Dollar" },
  { code: "EUR", name: "Euro" },
  { code: "GBP", name: "British Pound" },
  { code: "JPY", name: "Japanese Yen" },
  { code: "AUD", name: "Australian Dollar" },
  { code: "CAD", name: "Canadian Dollar" },
  { code: "PHP", name: "Philippine Peso" },
  { code: "SGD", name: "Singapore Dollar" },
];

const TIMEZONES = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Los_Angeles",
  "Europe/London",
  "Europe/Paris",
  "Asia/Tokyo",
  "Asia/Shanghai",
  "Asia/Manila",
  "Asia/Singapore",
  "Australia/Sydney",
];

interface TeamRegionalPreferencesTabProps {
  teamId: string;
}

export function TeamRegionalPreferencesTab({ teamId }: TeamRegionalPreferencesTabProps) {
  
  const [currency, setCurrency] = useState("USD");
  const [timezone, setTimezone] = useState("UTC");
  const [unitSystem, setUnitSystem] = useState("metric");

  const { data: preferences, isLoading } = useQuery({
    queryKey: ["/api/team/preferences", teamId],
    queryFn: async () => {
      return apiRequest("GET", `/api/team/${teamId}/preferences`) as unknown as { currency: string; timezone: string; unitSystem: string };
    },
  });

  useEffect(() => {
    if (preferences) {
      setCurrency(preferences.currency || "USD");
      setTimezone(preferences.timezone || "UTC");
      setUnitSystem(preferences.unitSystem || "metric");
    }
  }, [preferences]);

  const updateMutation = useApiMutation({
    endpoint: `/api/team/${teamId}/preferences`,
    method: "PATCH",
    invalidate: [["/api/team/preferences"]],
    successMessage: "Team preferences updated successfully",
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Regional Preferences</CardTitle>
        <CardDescription>
          Set default currency, timezone, and unit system for your team. These settings will apply to all team members when working in this workspace.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="currency">Currency</Label>
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger id="currency">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CURRENCIES.map((curr) => (
                <SelectItem key={curr.code} value={curr.code}>
                  {curr.code} - {curr.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="timezone">Timezone</Label>
          <Select value={timezone} onValueChange={setTimezone}>
            <SelectTrigger id="timezone">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIMEZONES.map((tz) => (
                <SelectItem key={tz} value={tz}>
                  {tz}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="unitSystem">Unit System</Label>
          <Select value={unitSystem} onValueChange={setUnitSystem}>
            <SelectTrigger id="unitSystem">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="metric">Metric (kg, cm, °C)</SelectItem>
              <SelectItem value="imperial">Imperial (lb, in, °F)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button
          onClick={() => updateMutation.mutate({ currency, timezone, unitSystem })}
          disabled={updateMutation.isPending}
        >
          {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Save Changes
        </Button>
      </CardContent>
    </Card>
  );
}
